import { action, runInAction, makeObservable } from 'mobx';
import { eventBus, subscribe } from 'mobx-event-bus2';
import { AxiosError } from 'axios';

import { reverse } from 'named-urls';

import Logger from 'utils/logger';

import API from 'utils/api';
import { EventType } from 'utils/events/constants';
import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';
import { EntityValueType } from 'utils/api/modules/entities/entities.types';
import { isNotFound } from 'utils/api/errors';

import { EntityModules } from 'core/constants';

import routes from 'core/routes';

import EntitiesActivities from 'stores/EntitiesActivities';

import RootStore from '../Root';
import EntityDetails from '../EntityDetails/EntityDetails';

export default class ContactsDetailsStore extends EntityDetails {
  constructor(rootStore: RootStore, api: typeof API) {
    super(rootStore, api, EntityModules.Contacts, routes.dashboard.contacts.toString(), (uuid: string): string =>
      reverse(routes.dashboard.contacts.details, { entityId: uuid })
    );

    makeObservable(this);

    eventBus.register(this);
  }

  @action.bound
  async loadDetails(contactId: string): Promise<void> {
    this.isLoaded = false;
    try {
      const { data } = await this.api.entities.loadEntitiesDetails(this.entityType, contactId)();
      runInAction(() => {
        this.activities = new EntitiesActivities(this.store, this.api, contactId, this.entityType);
        this.error = undefined;
        this.details = data;
      });
    } catch (e) {
      runInAction(() => {
        // @ts-ignore
        this.error = e;
      });
      // @ts-ignore
      if (!isNotFound(e)) {
        Logger.error(`Invalid load contact details API response. Task id: ${contactId}`, e);
      }
    } finally {
      runInAction(() => {
        this.isLoaded = true;
      });
    }
  }

  create = async (contactName: string): Promise<boolean | string> => {
    try {
      const {
        data: { uuid },
      } = await this.api.entities.createEntity(this.entityType, { [`${this.prefix}name`]: contactName })();
      return uuid;
    } catch (e) {
      Logger.error('Failed to create contact', e);

      return false;
    }
  };

  delete = async (contactId: string): Promise<boolean> => {
    try {
      await this.api.entities.deleteEntity(this.entityType, contactId)();
    } catch (e) {
      Logger.error('Failed to delete contact', e);
      return false;
    }
    return true;
  };

  edit = async (data: Record<string, EntityValueType>): Promise<boolean> => {
    if (this.details?.uuid === undefined) {
      return false;
    }
    try {
      const edited = await this.api.entities.editEntity(this.entityType, this.details?.uuid, data)();
      this.details = edited.data;
      this.isContentEdited = false;
      this.store.notification.enqueueSuccessSnackbar('Contact edited successfully');
      return true;
    } catch (e) {
      const error = e as AxiosError<string[]>;
      this.store.notification.enqueueErrorSnackbar(
        error.response?.data?.[0] || `The contact could not be updated. Something went wrong.`
      );
      Logger.error('Failed to edit contact', e);
      return false;
    }
  };

  @subscribe(EventType.LoggedOut)
  @action
  reset(): void {
    this.isLoaded = false;
    this.error = undefined;
    this.details = undefined;
  }

  @action.bound
  async loadCount(): Promise<number> {
    this.isLoaded = false;
    try {
      const { data } = await this.api.entities.loadEntitiesCount(EntityModules.Contacts)();
      runInAction(() => {
        this.error = undefined;
      });
      return data.count;
    } catch (e) {
      runInAction(() => {
        // @ts-ignore
        this.error = e;
      });
      return 0;
    } finally {
      runInAction(() => {
        this.isLoaded = true;
      });
    }
  }

  @action.bound
  contentHasBeenEdited(questionId: string, newValue: AnswerTypes): void {
    const question = this.details?.entity[questionId];
    if (question?.value !== newValue) {
      this.isContentEdited = true;
    } else {
      this.isContentEdited = false;
    }
  }
}
